import React, { useEffect, useState } from 'react';

import { 
  Settings, ShieldCheck, PieChart, Users, 
  FileText, HeartPulse, Zap, Globe, 
  ChevronRight, CheckCircle2 
} from 'lucide-react';
import SEO from '../seo/SEO';

const HROutsourcingPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', id: '/', isPage: true },
    { name: 'About Us', id: '/about', isPage: true },
    { name: 'Services', id: 'services' },
    { name: 'Career', id: 'career' },
    { name: 'Contact', id: '/contact', isPage: true },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SEO
        title="HR Outsourcing Services | GlobalEPRC"
        description="GlobalEPRC offers reliable HR outsourcing solutions covering payroll, compensation & benefits, insurance, recruitment and administration, helping organizations simplify HR operations."
        path="/hr-outsourcing"
      />

      {/* 1. HERO SECTION */}
      <section className="pt-48 pb-20 bg-slate-900 relative overflow-hidden text-white">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gold-gradient opacity-10 blur-[120px]"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-white/10 text-amber-400 px-4 py-1.5 rounded-full mb-8 backdrop-blur-md border border-white/10 font-bold text-xs uppercase tracking-[0.2em]">
                <ShieldCheck className="h-4 w-4" /> Operational Excellence
              </div>
              <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
                HR <span className="text-gold-gradient">Outsourcing</span>
              </h1>
              <p className="text-justify text-xl text-slate-300 leading-relaxed mb-8">
                GlobalEPRC offers reliable outsourcing solutions focused on HR, Payroll management, Compensation & benefits, Insurance, Recruitment, and Administration, helping organizations simplify HR operations and ensure statutory compliance.
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-semibold text-amber-100">
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Operational Efficiency</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Cost Control</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Regulatory Compliance</span>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
               <div className="absolute -inset-4 bg-gold-gradient rounded-[3rem] blur opacity-20"></div>
               <img 
                 src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800" 
                 alt="HR Management" 
                 className="relative rounded-[2.5rem] shadow-2xl border border-white/10"
               />
            </div>
          </div>
        </div>
      </section>

      {/* 2. CORE VALUE PROPOSITION */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Simplify Your Workforce Management</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              By outsourcing the HR functions to us, organizations benefit from operational efficiency, cost control, confidentiality, and regulatory compliance, while focusing on core business priorities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "Payroll Management", 
                icon: PieChart, 
                desc: "Our payroll services include salary processing, statutory deductions, tax compliance, payslip generation, and accurate reporting—ensuring timely and error-free payroll management." 
              },
              { 
                title: "Compensation & Benefits", 
                icon: HeartPulse, 
                desc: "Our compensation & benefits services cover salary structuring, incentives, reimbursements, employee benefits administration, and compliance management." 
              },
              { 
                title: "Insurance & Compliance", 
                icon: Users, 
                desc: "Our insurance and compliance services ensure your workforce is protected and your organization remains Compliance with all statutory requirements." 
              }
            ].map((item, i) => (
              <div key={i} className="group p-10 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl transition-all duration-500">
                <div className="bg-white p-5 rounded-2xl shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all w-fit mb-8">
                  <item.icon className="h-8 w-8 text-amber-600 group-hover:text-white" />
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. DETAILED SERVICES SECTION */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          
          {/* Payroll Detailed */}
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-32">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img src="/pay1.jpg" className="rounded-2xl shadow-lg mt-8" alt="Payroll Processing" />
                <img src="/pay2.jpg" className="rounded-2xl shadow-lg" alt="Data Reporting" />
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <h3 className="text-amber-600 font-bold uppercase tracking-widest text-sm mb-4">Service Deep Dive</h3>
              <h2 className="text-4xl font-black text-slate-900 mb-6">Payroll Management</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We handle the complexities of salary processing and tax compliance, so you don't have to. Our systems ensure error-free payroll management and accurate reporting for your entire workforce.
              </p>
              <ul className="grid sm:grid-cols-2 gap-4">
                {['Salary Processing', 'Statutory Deductions', 'Tax Compliance', 'Payslip Generation'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 text-sm font-bold">
                    <div className="bg-amber-100 p-1 rounded-md"><FileText className="h-4 w-4 text-amber-700" /></div> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Benefits & Insurance Detailed */}
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-32">
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-black text-slate-900 mb-6">Compensation & Benefits</h2>
              <p className=" text-justify text-lg text-slate-600 leading-relaxed mb-8">
                Our services include employee insurance management, such as medical insurance, life insurance, and statutory coverage as per local labor regulations—supporting employee satisfaction and retention.
              </p>
              <div className="p-8 bg-amber-50 rounded-3xl border border-amber-100">
                <h4 className="font-bold text-amber-900 mb-4 flex items-center gap-2"><HeartPulse className="h-5 w-5" /> Comprehensive Coverage</h4>
                <p className="text-sm text-amber-800/80 leading-relaxed">
                  Salary structuring, incentives, reimbursements, and employee benefits administration are designed to support employee satisfaction and retention.
                </p>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img src="/emp.jpg" className="rounded-[3rem] shadow-2xl" alt="Employee Benefits" />
            </div>
          </div>

          {/* insurance */}
                    <div className="flex flex-col lg:flex-row gap-16 items-center mb-32">
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-black text-slate-900 mb-6">Insurance & Compliance</h2>
              <p className="text-justify text-lg text-slate-600 leading-relaxed mb-8">
            We offer comprehensive Insurance and Compliance support as part of our HR outsourcing services, ensuring your workforce is protected and your organization remains fully compliant with statutory and regulatory requirements.<br></br>

Our services include employee health insurance, group medical coverage, workmen compensation, accident insurance, and statutory compliances. We manage policy coordination, renewals, documentation, audits, and compliance reporting, reducing risk and administrative burden for employers. <br></br>

With our expert handling of insurance and compliance matters, clients can focus on core business operations while we ensure legal adherence, employee welfare, and operational peace of mind.
              </p>       
            </div>
            <div className="lg:w-1/2">
              <img src="/insurance.jpeg" className="rounded-[3rem] shadow-2xl" alt="Employee Benefits" />
            </div>
          </div>

          {/* RPO Detailed */}
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold-gradient rounded-full blur-3xl opacity-10 animate-pulse"></div>
                <img src="/outsourcing.jpeg" className="rounded-3xl relative z-10 shadow-xl" alt="Global Recruitment" />
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <h2 className="text-4xl font-black text-slate-900 mb-6">Recruitment Outsourcing</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 text-justify">
                We manage sourcing, screening, selection, documentation, and deployment—acting as an extension of your HR team. With our global talent network, we deliver reliable manpower across all industries quickly and efficiently.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-slate-700 font-bold"><Zap className="text-amber-500 h-5 w-5" /> Hire Faster</div>
                <div className="flex items-center gap-4 text-slate-700 font-bold"><Globe className="text-amber-500 h-5 w-5" /> Global Talent Network</div>
                <div className="flex items-center gap-4 text-slate-700 font-bold"><Settings className="text-amber-500 h-5 w-5" /> End-to-End Management</div>
              </div>
            </div>
          </div>

                    {/* Benefits & Insurance Detailed */}


        </div>
      </section>
    </div>
  );
};

export default HROutsourcingPage;